import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomer } from '../customer-context.js';
import { useAgentName } from '../agent-name.js';
import { useContent } from '../content.js';
import { api } from '../api/client.js';
import infoIcon from '../assets/info-icon.svg';

/** Card / info-box wrapper matching the PTSB design-system `InfoBox`. */
function InfoBox({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <div
      className={`bg-white border-2 border-[#dad7d3] p-6 shadow-sm ${className}`}
    >
      {children}
    </div>
  );
}

type CheckStatus = 'pass' | 'fail' | null;

/**
 * Accessible pass/fail radio pair, faithful to the export's `AccessibleRadio`
 * (shared with the Medium / High security ports). Drives the outbound DoB
 * confirmation check.
 */
function PassFailRadios({
  name,
  value,
  onChange,
}: {
  name: string;
  value: CheckStatus;
  onChange: (v: 'pass' | 'fail') => void;
}): React.JSX.Element {
  return (
    <div
      className="flex items-center gap-6"
      role="radiogroup"
      aria-label={`${name} verification result`}
    >
      {(['pass', 'fail'] as const).map((option) => {
        const id = `${name}-${option}`;
        const checked = value === option;
        return (
          <div key={option} className="flex items-center gap-3">
            <input
              type="radio"
              id={id}
              name={name}
              checked={checked}
              onChange={() => onChange(option)}
              className="sr-only peer"
            />
            <label
              htmlFor={id}
              className="relative flex items-center cursor-pointer group"
            >
              <div className="relative size-[24px] rounded-full border-2 border-[#979c9f] transition-all bg-white group-hover:border-[#6b7280] peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]">
                {checked && (
                  <div className="absolute inset-0 m-auto size-[12px] rounded-full bg-[#1d252c]" />
                )}
              </div>
              <span className="ml-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] select-none capitalize">
                {option}
              </span>
            </label>
          </div>
        );
      })}
    </div>
  );
}

/** Formats an ISO `YYYY-MM-DD` date of birth as `DD/MM/YYYY` for display. */
function formatDob(dob: string | undefined): string {
  if (!dob) return 'Not available';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dob);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : dob;
}

/**
 * The customer's first name, derived from the in-context full name for the
 * scripting greeting (`Hi (customer), …`). Empty when no name is on file — the
 * caller then falls back to a neutral cue.
 */
function firstName(name: string | undefined): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? '';
}

/**
 * SCRN 11 — Outbound Security (CCF-105).
 *
 * Faithful port of the customer's Figma "update outbound security" export. The
 * agent has already placed the OUTBOUND call from the Genesys workspace, then
 * follows the mandatory regulatory/compliance scripting in the exact order and
 * completes the outbound security step (Date-of-Birth confirmation) to confirm
 * the customer's identity before discussing the account.
 *
 * Architecture:
 *  - **Call initiation is Genesys-native / swivel** — there is no Bank API to
 *    "place a call", and per @dylan219 (#491, 25 Jul) the queue/number selection
 *    + "Place outbound call" control are NOT surfaced on this screen at all.
 *    The agent originates the outbound call from the Genesys workspace; this
 *    screen is the scripting + outbound-security step only. A "Back to
 *    dashboard" control returns the agent to the customer dashboard.
 *  - **The security step is the outbound DoB confirmation only.** Per CCF-105
 *    (update 11/Jun) **ID&V is DEACTIVATED in the offline webapp for outbound
 *    scripting** — this screen therefore does NOT route to the ID&V screen. If
 *    the customer requests an account action the agent is instructed to switch
 *    to the agent workspace / online flow to log the customer in; there is no
 *    "To ID&V" affordance here. This screen creates NO new validation routes.
 *
 * Unhappy path (CCF-105): a failed DoB confirmation advises the agent they are
 * unable to proceed. Per #688 the call-recording-consent and suitable-time
 * pass/fail questions are NOT displayed at all; the DoB confirmation renders
 * directly on entry.
 */
export function OutboundSecurityScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';

  // Outbound DoB confirmation — the security/identity check.
  const [dobStatus, setDobStatus] = useState<CheckStatus>(null);

  const dobDisplay = formatDob(customer.dob);
  const hasDob = Boolean(customer.dob);
  // #486 / #608: populate the regulatory-script variables with the live
  // identified customer and the logged-in agent's display name. Fall back to
  // neutral placeholder cues when data is unavailable so the script still reads
  // sensibly (e.g. Cognito sandbox sessions do not carry a name claim).
  const agentName = useAgentName();

  // #637 (SCRN-11 / CCF-113) — the outbound DoB-mismatch advisory copy is
  // admin-editable content-catalogue text (non-regulatory failure guidance),
  // resolved via `useContent` with the compiled-in default as its fallback so
  // the screen renders unchanged when there is no override. Rendered as PLAIN
  // TEXT only (no HTML). The security/DoB-confirmation LOGIC is unchanged — only
  // the literal advisory strings are reworded.
  const dobFailMessage = useContent('outbound.security.dob_fail');

  const customerFullName = (customer.name ?? '').trim();
  const scriptFullName = customerFullName || "[customer's full name]";
  const scriptFirstName = firstName(customer.name) || '(customer)';
  // #608 CCF-105: render the agent's actual name from the session; only fall
  // back to the parenthetical cue when the IdP did not issue a name claim.
  const scriptAgentName = agentName ?? '(agent full name)';

  // #637 (SCRN-11, CCF-113) — the regulatory OUTBOUND opening call script is
  // admin-editable content (text only, never logic). Each paragraph resolves to
  // its baked-in default until an admin reword is fetched; rendered as PLAIN
  // TEXT. The greeting/intro defaults carry literal placeholder tokens
  // (`{scriptFullName}` / `{scriptFirstName}` / `{scriptAgentName}`) that are
  // substituted with the live runtime values at render — this is presentation,
  // not editable logic. `fillScript` is best-effort: if an admin removes a
  // token it simply isn't substituted (fail-safe, never crashes).
  const scriptRegulatoryHeading = useContent(
    'outbound.script.regulatory_heading',
  );
  const scriptGreeting = useContent('outbound.script.greeting');
  const scriptIntro = useContent('outbound.script.intro');
  const scriptSuitableTime = useContent('outbound.script.suitable_time');
  const scriptConfirmDob = useContent('outbound.script.confirm_dob');
  const fillScript = (t: string): string =>
    t
      .replaceAll('{scriptFullName}', scriptFullName)
      .replaceAll('{scriptFirstName}', scriptFirstName)
      .replaceAll('{scriptAgentName}', scriptAgentName);

  const dobFailed = dobStatus === 'fail';
  const dobConfirmed = dobStatus === 'pass';
  // The identity check is complete (→ success + workspace-switch guidance) only
  // when the DoB is actually on file and confirmed. Per @dylan219 (#491, 25 Jul)
  // call initiation is Genesys-native and no longer surfaced here, so the
  // scripting + DoB check render directly on entry.
  const outboundIdentityConfirmed = hasDob && dobConfirmed;

  // CCF-112 (#477): record the Outbound security DoB check's TERMINAL outcome
  // (Passed / Failed) as an `Outbound Security Result` Non-API business event,
  // mirroring the shared Medium/High + ECL + Broker security screens' fire via
  // the existing `/calls/security-event` route (a new `outbound` level). The
  // event is fired best-effort, exactly once per terminal outcome; a
  // Call-History write failure must NEVER break the flow.
  //
  // Stale-outcome-crosses-customers guard (#243/#244/#422): the customer context
  // can swap while this screen is mounted, and the local pass/fail state does NOT
  // auto-reset — so a prior customer's DoB 'pass' could otherwise fire against a
  // NEW CIF. `dobOutcomeCifRef` captures the CIF SYNCHRONOUSLY at the moment the
  // DoB radio is clicked (the only moment the outcome becomes terminal and
  // `customerNumber` is unambiguously correct), so the fire is race-proof
  // regardless of effect ordering. `securityEventFiredRef` keys the once-guard to
  // the CIF (once per customer per outcome).
  const dobOutcomeCifRef = useRef<string>('');
  const securityEventFiredRef = useRef<string | null>(null);

  // Wrap the DoB Pass/Fail set to capture the current CIF at click-time.
  function handleDobResult(v: 'pass' | 'fail'): void {
    dobOutcomeCifRef.current = customerNumber;
    setDobStatus(v);
  }

  // New customer ⇒ a fresh outbound scripting session: reset the flow state and
  // clear both refs. UX-correct AND belt-and-braces for the guard (the
  // synchronous CIF-capture above is what makes the fire race-proof regardless
  // of order).
  useEffect(() => {
    setDobStatus(null);
    dobOutcomeCifRef.current = '';
    securityEventFiredRef.current = null;
  }, [customerNumber]);

  useEffect(() => {
    if (securityEventFiredRef.current === customerNumber) return;
    if (!customerNumber) return;
    // The captured CIF must match the current customer — a terminal outcome can
    // never be attributed to a customer who swapped in after the DoB click.
    if (dobOutcomeCifRef.current !== customerNumber) return;
    const outcome: 'Passed' | 'Failed' | null = outboundIdentityConfirmed
      ? 'Passed'
      : dobFailed
        ? 'Failed'
        : null;
    if (!outcome) return;
    securityEventFiredRef.current = customerNumber;
    void api
      .POST('/calls/security-event', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: { customerNumber, securityLevel: 'outbound', outcome },
      })
      .catch(() => {
        // Best-effort: a Call-History write failure must never break the flow.
      });
    // `tellerId` is read at fire-time only; excluded to keep the effect keyed to
    // the terminal-outcome transition for the current customer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outboundIdentityConfirmed, dobFailed, customerNumber]);

  return (
    <main
      className="max-w-[800px] pl-12 pr-12 py-12"
      aria-labelledby="outbound-heading"
    >
      {/* #491 (dylan219, 25 Jul): "Back to dashboard" control at the top of the
          page — the agent placed the call from the Genesys workspace, so this
          screen is scripting + outbound security only. */}
      <div className="mb-6">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          aria-label="Back to customer dashboard"
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] font-bold text-[#005fcc] hover:underline focus:outline-none focus-visible:outline focus-visible:outline-4 focus-visible:outline-offset-2 focus-visible:outline-[#005fcc]"
        >
          <span aria-hidden="true">&larr;</span> Back to dashboard
        </button>
      </div>

      <div className="mb-6">
        <h1
          id="outbound-heading"
          className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0"
        >
          Outbound security
        </h1>
      </div>

      <div className="space-y-6">
        {/* Outbound scripting — regulatory requirement, must be followed in the
            exact order shown (faithful to the Figma copy + CCF-105). */}
        <InfoBox className="border-l-[#FF703E] border-l-[6px]">
          <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 mb-2">
            {scriptRegulatoryHeading}
          </p>
          <hr className="border-0 border-t border-[#dad7d3] my-4" />
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2">
            {fillScript(scriptGreeting)}
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2">
            {fillScript(scriptIntro)}
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2 ml-6">
            a. Previous Query
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2 ml-6">
            b. Account
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2 ml-6">
            c. Card
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2">
            Before I begin, please be advised calls will be recorded for{' '}
            <span className="font-bold">quality, training and regulatory</span>{' '}
            purposes.
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-2">
            {scriptSuitableTime}
          </p>
          <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mb-0">
            {scriptConfirmDob}
          </p>
        </InfoBox>

        {/* Outbound security — Date-of-Birth confirmation (CCF-105 customer
            identification). Rendered directly on entry. */}
        <InfoBox>
          <div className="flex items-center gap-4">
            <div className="flex flex-col">
              <h3 className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-1">
                Date of birth:
              </h3>
              <p className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                {dobDisplay}
              </p>
            </div>
            {/* Only offer the pass/fail check when a DoB is actually on file
                    — otherwise there is nothing to verify against and the agent
                    must proceed via full ID&V. */}
            {hasDob ? (
              <div className="ml-auto mt-5 flex items-center gap-6">
                <PassFailRadios
                  name="dob"
                  value={dobStatus}
                  onChange={handleDobResult}
                />
                {dobFailed && (
                  <span
                    className="px-6 py-1 rounded-md font-['Arial',sans-serif] font-bold text-[16px] bg-[#dc2626] text-white border-2 border-[#dc2626]"
                    aria-label="Verification failed"
                  >
                    FAIL
                  </span>
                )}
              </div>
            ) : (
              <div className="ml-auto mt-5 flex flex-col items-start gap-3 max-w-[420px]">
                <p
                  className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0"
                  role="note"
                >
                  No date of birth on file to verify against — do not confirm
                  identity here. ID&amp;V is deactivated in the offline outbound
                  flow; if the customer needs an account action, switch to the
                  agent workspace to log them in.
                </p>
              </div>
            )}
          </div>
        </InfoBox>

        {/* DoB incorrect — unable to proceed (CCF-105 #2). */}
        {dobFailed && (
          <div className="bg-[#F8EEE3] border-2 border-[#979C9F] p-6 shadow-sm flex gap-3">
            <img
              src={infoIcon}
              alt=""
              aria-hidden="true"
              className="w-5 h-5 mt-1 shrink-0"
            />
            <div>
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0 whitespace-pre-line">
                {dobFailMessage}
              </p>
            </div>
          </div>
        )}

        {/* DoB confirmed — the customer is identified for the call; the agent
            can proceed with the query. Per CCF-105 (11/Jun) ID&V is DEACTIVATED
            in the offline outbound flow, so there is no "To ID&V" affordance: if
            the customer requests an account action the agent switches to the
            agent workspace / online flow to log them in. */}
        {outboundIdentityConfirmed && (
          <div className="space-y-6">
            <div className="bg-[#d4edda] border-2 border-[#28a745] p-6 shadow-sm">
              <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
                Date of birth confirmed. You can proceed with the
                customer&rsquo;s query.
              </p>
              <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 mt-2">
                If the customer requests an action on their account, switch to
                the agent workspace and log them in with their Online card
                number and 3 PAN digits, then complete Medium Security. ID&amp;V
                is not available from the offline outbound flow.
              </p>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
